import React, { useEffect, useState } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ChartBar, Users, Buildings, Wrench, MapTrifold, BookOpen, Question, Gear, SignOut, House } from "@phosphor-icons/react";
import logo from "@/assets/360view-logo.jpg";

export default function AdminLayout() {
  const { user, checked, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (checked && !user) nav("/login", { replace: true, state: { from: window.location.pathname } });
  }, [checked, user, nav]);

  if (!checked) return <div className="min-h-screen bg-obsidian text-white/60 flex items-center justify-center">Loading…</div>;
  if (!user) return null;

  const links = [
    { to: "/admin", end: true, label: "Overview", icon: ChartBar },
    { to: "/admin/leads", label: "Leads", icon: Users },
    { to: "/admin/systems", label: "Systems", icon: Wrench },
    { to: "/admin/industries", label: "Industries", icon: Buildings },
    { to: "/admin/services", label: "Services", icon: Wrench },
    { to: "/admin/cities", label: "Cities", icon: MapTrifold },
    { to: "/admin/articles", label: "Articles", icon: BookOpen },
    { to: "/admin/faqs", label: "FAQs", icon: Question },
    { to: "/admin/settings", label: "Business Settings", icon: Gear },
  ];

  return (
    <div className="min-h-screen bg-obsidian text-white grid grid-cols-1 lg:grid-cols-[260px_1fr]">
      <aside className="border-r border-chrome bg-steel lg:sticky lg:top-0 lg:h-screen flex lg:flex-col">
        <div className="p-5 border-b border-chrome flex items-center gap-3">
          <div className="bg-white p-1"><img src={logo} alt="360 View LLC" className="h-10 w-auto block" /></div>
          <div>
            <div className="font-display font-black text-white text-base">360 VIEW</div>
            <div className="eyebrow text-[10px] text-solar">Admin</div>
          </div>
        </div>
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-semibold uppercase tracking-wider border-l-2 ${
                  isActive ? "bg-obsidian text-solar border-solar" : "text-white/70 border-transparent hover:text-solar hover:border-solar/40"
                }`
              }
            >
              <l.icon size={16} weight="fill" /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-chrome space-y-2">
          <Link to="/" data-testid="admin-view-site" className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-solar">
            <House size={14} weight="fill" /> View site
          </Link>
          <div className="text-xs text-white/50">{user.email}</div>
          <button data-testid="admin-logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 hover:text-solar">
            <SignOut size={14} weight="fill" /> Sign out
          </button>
        </div>
      </aside>
      <main className="p-6 md:p-10">
        <Outlet />
      </main>
    </div>
  );
}
